"use client"

import { useEffect, useState } from "react"
import { X, Eye, EyeOff, Mail, Lock, User as UserIcon, ArrowRight, ArrowLeft } from "lucide-react"
import { motion, AnimatePresence } from "framer-motion"
import { useSignIn, useSignUp, useAuth as useClerkAuth } from "@clerk/clerk-react"
import { useNavigate, useLocation } from "react-router-dom"
import { HAS_CLERK_PUBLISHABLE_KEY } from "../lib/config"

/* Reusable input — white bg, black text, gray border */
function StyledInput({ icon: Icon, error, rightSlot, className = "", ...props }) {
  return (
    <div className="relative">
      {Icon && (
        <Icon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-500 pointer-events-none" />
      )}
      <input
        {...props}
        className={[
          "w-full py-2.5 bg-white border rounded-md text-black placeholder-gray-400",
          "focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black/40",
          Icon ? "pl-10" : "pl-3",
          rightSlot ? "pr-10" : "pr-3",
          error ? "border-red-500" : "border-gray-300",
          className,
        ].join(" ")}
      />
      {rightSlot && (
        <div className="absolute right-3 top-1/2 -translate-y-1/2">{rightSlot}</div>
      )}
    </div>
  )
}

/* Primary button — black bg, white text */
function PrimaryButton({ children, loading, ...props }) {
  return (
    <motion.button
      whileHover={{ scale: 1.01 }}
      whileTap={{ scale: 0.98 }}
      {...props}
      className={[
        "w-full bg-black hover:bg-gray-900",
        "text-white py-3 px-4 rounded-full font-medium",
        "disabled:opacity-50 disabled:cursor-not-allowed",
        "flex items-center justify-center gap-2 transition",
        props.className || "",
      ].join(" ")}
    >
      {loading ? (
        <>
          <svg className="animate-spin h-5 w-5 text-white" viewBox="0 0 24 24">
            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" fill="none" />
            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
          </svg>
          <span>Please wait...</span>
        </>
      ) : (
        children
      )}
    </motion.button>
  )
}

function CustomLoginPopup({ onClose, redirectTo }) {
  const { isLoaded, isSignedIn } = useClerkAuth()
  const { signIn, setActive: setSignInActive } = useSignIn()
  const { signUp, setActive: setSignUpActive } = useSignUp()
  const navigate = useNavigate()
  const location = useLocation()

  // If no onClose provided (used as full page), navigate home on close
  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      navigate("/", { replace: true })
    }
  }

  const [mode, setMode] = useState("signin") // signin | signup | forgot | verify | verify-reset
  const [firstName, setFirstName] = useState("")
  const [lastName, setLastName] = useState("")
  const [email, setEmail] = useState("")
  const [password, setPassword] = useState("")
  const [code, setCode] = useState("")
  const [showPassword, setShowPassword] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState("")
  const [info, setInfo] = useState("")

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "auto" }
  }, [])

  useEffect(() => {
    if (isLoaded && isSignedIn) {
      const target = redirectTo || location.state?.from?.pathname || "/"
      handleClose()
      navigate(target, { replace: true })
    }
  }, [isLoaded, isSignedIn, redirectTo, location.state, navigate])

  const switchMode = (next) => {
    setMode(next)
    setError("")
    setInfo("")
  }

  const handleSignIn = async (e) => {
    e.preventDefault()
    if (!signIn || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      const result = await signIn.create({ identifier: email, password })
      if (result.status === "complete") {
        await setSignInActive({ session: result.createdSessionId })
      } else if (
        result.status === "needs_first_factor" ||
        result.status === "needs_second_factor"
      ) {
        // Clerk requires additional verification (new device, etc.)
        // Prepare email code verification
        const emailFactor = result.supportedFirstFactors?.find(
          (f) => f.strategy === "email_code"
        )
        if (emailFactor) {
          await signIn.prepareFirstFactor({
            strategy: "email_code",
            emailAddressId: emailFactor.emailAddressId,
          })
          setInfo("A verification code has been sent to your email")
          switchMode("verify-signin")
        } else {
          setError("Verification required but no email method available. Please try again.")
        }
      } else {
        setError("Sign in could not be completed. Please try again.")
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.message || "Sign in failed")
    } finally {
      setLoading(false)
    }
  }

  const handleVerifySignIn = async (e) => {
    e.preventDefault()
    if (!signIn || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "email_code",
        code,
      })
      if (result.status === "complete") {
        await setSignInActive({ session: result.createdSessionId })
      } else {
        setError("Verification failed. Please try again.")
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.message || "Invalid code")
    } finally {
      setLoading(false)
    }
  }

  const handleSignUp = async (e) => {
    e.preventDefault()
    if (!signUp || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      const result = await signUp.create({
        emailAddress: email,
        password,
        firstName: firstName.trim() || undefined,
        lastName: lastName.trim() || undefined,
      })
      if (result.status === "complete") {
        await setSignUpActive({ session: result.createdSessionId })
      } else {
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" })
        setInfo("We sent a verification code to your email")
        switchMode("verify")
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || err.message || "Sign up failed")
    } finally {
      setLoading(false)
    }
  }

  const handleVerify = async (e) => {
    e.preventDefault()
    if (!signUp || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      const result = await signUp.attemptEmailAddressVerification({ code })
      if (result.status === "complete") {
        await setSignUpActive({ session: result.createdSessionId })
      } else {
        setError("Verification incomplete")
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || "Invalid code")
    } finally {
      setLoading(false)
    }
  }

  const handleForgotPassword = async (e) => {
    e.preventDefault()
    if (!signIn || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      await signIn.create({ strategy: "reset_password_email_code", identifier: email })
      setInfo("Reset code sent to your email")
      switchMode("verify-reset")
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || "Failed to send reset code")
    } finally {
      setLoading(false)
    }
  }

  const handleResetPassword = async (e) => {
    e.preventDefault()
    if (!signIn || !isLoaded) return
    setLoading(true)
    setError("")
    try {
      const result = await signIn.attemptFirstFactor({
        strategy: "reset_password_email_code",
        code,
        password,
      })
      if (result.status === "complete") {
        await setSignInActive({ session: result.createdSessionId })
      }
    } catch (err) {
      setError(err.errors?.[0]?.longMessage || "Reset failed")
    } finally {
      setLoading(false)
    }
  }

  const titleByMode = {
    signin: "Welcome Back",
    signup: "Create Your Account",
    forgot: "Reset Password",
    verify: "Verify Your Email",
    "verify-reset": "Set New Password",
  }
  const subtitleByMode = {
    signin: "Sign in to continue to VillaKosh",
    signup: "Join VillaKosh — discover luxury villas",
    forgot: "We'll send a code to your email",
    verify: `Enter the 6-digit code sent to ${email}`,
    "verify-reset": "Enter your code and a new password",
  }

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        /* Centered both axes on every screen. z-[100] sits above LogoutConfirmationPopup (z-50) */
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100] overflow-y-auto"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0, y: 20 }}
          animate={{ scale: 1, opacity: 1, y: 0 }}
          exit={{ scale: 0.92, opacity: 0, y: 20 }}
          transition={{ type: "spring", damping: 18 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md relative my-auto"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button
            onClick={handleClose}
            className="absolute top-4 right-4 z-10 text-gray-500 hover:text-black transition"
            aria-label="Close"
          >
            <X size={22} />
          </button>

          {/* Back button (when in a sub-flow) */}
          {(mode === "forgot" || mode === "verify" || mode === "verify-reset" || mode === "verify-signin") && (
            <button
              onClick={() => switchMode(mode === "verify" ? "signup" : "signin")}
              className="absolute top-4 left-4 z-10 text-gray-500 hover:text-black transition flex items-center gap-1 text-sm"
              aria-label="Back"
            >
              <ArrowLeft size={18} />
            </button>
          )}

          <div className="p-8">
            {/* Title */}
            <motion.div
              initial={{ y: -10, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.1 }}
              className="text-center mb-6"
            >
              <h1 className="text-2xl font-bold text-black">{titleByMode[mode]}</h1>
              <p className="text-sm text-gray-600 mt-1">{subtitleByMode[mode]}</p>
            </motion.div>

            {/* Notice */}
            {info && !error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-green-50 border border-green-200 text-green-800 text-sm p-3 rounded-xl mb-4"
              >
                {info}
              </motion.div>
            )}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: -6 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-50 border border-red-200 text-red-800 text-sm p-3 rounded-xl mb-4"
              >
                {error}
              </motion.div>
            )}

            {/* ───── Sign in ───── */}
            {mode === "signin" && (
              <motion.form
                key="signin"
                onSubmit={handleSignIn}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Email</label>
                  <StyledInput
                    icon={Mail}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Password</label>
                  <StyledInput
                    icon={Lock}
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter your password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="text-gray-500 hover:text-black"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                </div>

                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={() => switchMode("forgot")}
                    className="text-xs font-medium text-gray-700 hover:text-black transition"
                  >
                    Forgot password?
                  </button>
                </div>

                <PrimaryButton type="submit" disabled={loading} loading={loading}>
                  Sign in
                  <ArrowRight size={18} />
                </PrimaryButton>

                <p className="text-center text-sm text-gray-600 pt-2">
                  New to VillaKosh?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signup")}
                    className="text-black hover:underline font-semibold"
                  >
                    Create account
                  </button>
                </p>
              </motion.form>
            )}

            {/* ───── Sign up ───── */}
            {mode === "signup" && (
              <motion.form
                key="signup"
                onSubmit={handleSignUp}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900">First name</label>
                    <StyledInput
                      icon={UserIcon}
                      type="text"
                      placeholder="First name"
                      value={firstName}
                      onChange={(e) => setFirstName(e.target.value)}
                      required
                      autoComplete="given-name"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="block text-sm font-semibold text-gray-900">Last name</label>
                    <StyledInput
                      type="text"
                      placeholder="Last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                      required
                      autoComplete="family-name"
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Email</label>
                  <StyledInput
                    icon={Mail}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>

                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Password</label>
                  <StyledInput
                    icon={Lock}
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="text-gray-500 hover:text-black"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                  <p className="text-xs text-gray-500">At least 8 characters with letters and numbers</p>
                </div>

                <PrimaryButton type="submit" disabled={loading} loading={loading}>
                  Create account
                  <ArrowRight size={18} />
                </PrimaryButton>

                <p className="text-center text-sm text-gray-600 pt-2">
                  Already have an account?{" "}
                  <button
                    type="button"
                    onClick={() => switchMode("signin")}
                    className="text-black hover:underline font-semibold"
                  >
                    Sign in
                  </button>
                </p>
              </motion.form>
            )}

            {/* ───── Forgot password ───── */}
            {mode === "forgot" && (
              <motion.form
                key="forgot"
                onSubmit={handleForgotPassword}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Email</label>
                  <StyledInput
                    icon={Mail}
                    type="email"
                    placeholder="you@example.com"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                  />
                </div>
                <PrimaryButton type="submit" disabled={loading} loading={loading}>
                  Send reset code
                  <ArrowRight size={18} />
                </PrimaryButton>
                <button
                  type="button"
                  onClick={() => switchMode("signin")}
                  className="block mx-auto text-sm text-gray-600 hover:text-black"
                >
                  Back to sign in
                </button>
              </motion.form>
            )}

            {/* ───── Verify email ───── */}
            {mode === "verify" && (
              <motion.form
                key="verify"
                onSubmit={handleVerify}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Verification code</label>
                  <input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md text-black text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black/40 placeholder-gray-400"
                  />
                </div>
                <PrimaryButton type="submit" disabled={loading || code.length !== 6} loading={loading}>
                  Verify
                  <ArrowRight size={18} />
                </PrimaryButton>
                <p className="text-center text-xs text-gray-500">
                  Didn't receive the code? Check your spam folder.
                </p>
              </motion.form>
            )}

            {/* ───── Reset password (after forgot) ───── */}
            {mode === "verify-reset" && (
              <motion.form
                key="verify-reset"
                onSubmit={handleResetPassword}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Verification code</label>
                  <input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md text-black text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black/40 placeholder-gray-400"
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">New password</label>
                  <StyledInput
                    icon={Lock}
                    type={showPassword ? "text" : "password"}
                    placeholder="Minimum 8 characters"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    rightSlot={
                      <button
                        type="button"
                        onClick={() => setShowPassword((v) => !v)}
                        className="text-gray-500 hover:text-black"
                      >
                        {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                      </button>
                    }
                  />
                </div>
                <PrimaryButton type="submit" disabled={loading} loading={loading}>
                  Reset password
                  <ArrowRight size={18} />
                </PrimaryButton>
              </motion.form>
            )}

            {/* ───── Verify sign-in (new device OTP) ───── */}
            {mode === "verify-signin" && (
              <motion.form
                key="verify-signin"
                onSubmit={handleVerifySignIn}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                className="space-y-4"
              >
                <p className="text-sm text-gray-600 text-center">
                  Enter the 6-digit code sent to <span className="font-medium text-black">{email}</span>
                </p>
                <div className="space-y-2">
                  <label className="block text-sm font-semibold text-gray-900">Verification code</label>
                  <input
                    type="text"
                    placeholder="000000"
                    value={code}
                    onChange={(e) => setCode(e.target.value.replace(/\D/g, "").slice(0, 6))}
                    required
                    maxLength={6}
                    inputMode="numeric"
                    autoComplete="one-time-code"
                    className="w-full px-3 py-3 bg-white border border-gray-300 rounded-md text-black text-center text-2xl tracking-[0.5em] focus:outline-none focus:ring-2 focus:ring-black/20 focus:border-black/40 placeholder-gray-400"
                  />
                </div>
                <PrimaryButton type="submit" disabled={loading || code.length < 6} loading={loading}>
                  Verify &amp; Sign in
                  <ArrowRight size={18} />
                </PrimaryButton>
              </motion.form>
            )}
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

function FallbackLoginPopup({ onClose }) {
  const navigate = useNavigate()

  const handleClose = () => {
    if (onClose) {
      onClose()
    } else {
      navigate("/", { replace: true })
    }
  }

  useEffect(() => {
    document.body.style.overflow = "hidden"
    return () => { document.body.style.overflow = "auto" }
  }, [])

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-[100]"
        onClick={handleClose}
      >
        <motion.div
          initial={{ scale: 0.92, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.92, opacity: 0 }}
          transition={{ type: "spring", damping: 18 }}
          className="bg-white rounded-3xl shadow-2xl w-full max-w-md p-8 text-center relative"
          onClick={(e) => e.stopPropagation()}
        >
          <button onClick={handleClose} className="absolute top-4 right-4 text-gray-500 hover:text-black">
            <X size={22} />
          </button>
          <h2 className="text-2xl font-bold text-black mb-2">Login unavailable</h2>
          <p className="text-gray-600 text-sm mb-6">
            Authentication isn't configured on this deployment yet.
          </p>
          <motion.button
            whileHover={{ scale: 1.01 }}
            whileTap={{ scale: 0.98 }}
            onClick={handleClose}
            className="w-full bg-black hover:bg-gray-900 text-white py-3 rounded-full font-medium transition"
          >
            Continue browsing
          </motion.button>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  )
}

export default function LoginPopup({ onClose, redirectTo }) {
  if (!HAS_CLERK_PUBLISHABLE_KEY) {
    return <FallbackLoginPopup onClose={onClose} />
  }
  return <CustomLoginPopup onClose={onClose} redirectTo={redirectTo} />
}
